import React, { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import { Button, Drawer, Menu } from "antd";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "redux/Dashboard/actions.js";
import { CloseOutlined, BarChartOutlined, PercentageOutlined } from "@ant-design/icons";
import DropdownSelect from "helperComponents/CustomDropdown";
import CustomChart from "components/Charts";
import { useTranslation } from "react-i18next";
const ComparisonChart = ({
  chartData,
  chartType,
  setSubCategoryClicked,
  setSelectedCategoryForSubcategories,
  setSelectedGroupForReviews,
  barColorMap,
  doughnutColorMap,
  dateRange,
  isNormalized,
  onToggleNormalized,
}) => {
  const comparisonData = chartData?.map(
    (dashboardData) => dashboardData?.comparisonData
  );
  const chartInstance = useRef(null);
  const [barType, setBarType] = useState(window.innerWidth > 949 ? "H" : "V");
  const [toggleSelection, setToggleSelection] = useState("Daily");
  const [activeBtn, setActiveBtn] = useState("Daily");
  const DashboardReducer = useSelector((state) => state.DashboardReducer);
  const theme = DashboardReducer.theme;
  const [isMobileView, setIsMobileView] = useState(
    window.innerWidth < 950 ? true : false
  );
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedDisplayType, setSelectedDisplayType] = useState(null);
  const { t } = useTranslation();
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 949) {
        setBarType("H");
        setIsMobileView(false);
      } else {
        setBarType("V");
        setIsMobileView(true);
      }
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [barType, isMobileView]);
  const categoryLabels = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.category)
  )[0];
  const categoryIds = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.categoryId)
  )[0];
  const dashboardLabels = chartData?.map(
    (dashboardData) => dashboardData?.dashboard
  );
  const dashboardIds = chartData?.map(
    (dashboardData) => dashboardData?.dashboardId
  );
  const totalReviews = chartData?.map(
    (dashboardData) => dashboardData.total_reviews
  );
  const percentages = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.percentage)
  );
  const labels = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.category)
  );
  const ids = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.categoryId)
  );
  const counts = comparisonData?.map((categoryData) =>
    categoryData?.map((data) => data?.count)
  );

  const datasets = [];
  const round = (value, precision) => {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  };
  if (labels?.length > 0 && ids?.length > 0 && percentages?.length > 0) {
    // Iterate through the arrays and rearrange the data
    for (let i = 0; i < labels.length; i++) {
      for (let j = 0; j < labels[i].length; j++) {
        const label = labels[i][j];
        const id = ids[i][j];
        let percentage = percentages[i][j];
        let count = counts[i][j]; // Get the raw count
        const existingData = datasets.find(
          (data) => data.label === label && data.id === id
        );
        if (!existingData) {
          datasets.push({
            label: label,
            id: id,
            percentageData: [percentage], // Store percentages separately
            countData: [count], // Store counts separately
            backgroundColor: barColorMap[id],
            barThickness:
              barType === "H" ? 25 : dashboardIds?.length > 6 ? 25 : 35,
            borderSkipped: false,
            borderRadius: {
              topLeft: 50,
              topRight: 50,
              bottomLeft: 50,
              bottomRight: 50,
            },
            order: j + 1,
          });
        } else {
          existingData.percentageData.push(percentage);
          existingData.countData.push(count);
        }
      }
    }
  }
  const adjustDatasets = (datasets) => {
    dashboardIds.forEach((_, index) => {
      let sum = datasets.reduce((acc, dataset) => acc + dataset.data[index], 0); // Calculate sum of first elements
      if (sum > 100) {
        let excess = sum - 100; // Calculate the excess amount
        // Find the dataset with the maximum first element
        let maxDataset = datasets.reduce((max, dataset) => {
          return dataset.data[index] > max.data[index] ? dataset : max;
        });
        // Reduce the maximum element by the excess amount
        maxDataset.data[index] -= excess;
      }
    });
    return datasets;
  };
  const data = {
    labels: dashboardLabels,
    datasets: isNormalized 
      ? adjustDatasets(datasets.map(dataset => ({
          ...dataset,
          data: dataset.percentageData // Use percentages when normalized
        })))
      : datasets.map(dataset => ({
          ...dataset,
          data: dataset.countData // Use raw counts when not normalized
        }))
  };

  // Define a function that takes the labels, ids, and counts arrays as parameters
  function rearrangeCounts(labels, ids, counts) {
    // Create an empty array to store the rearranged counts
    let rearrangedCounts = [];
    if (labels?.length > 0 && ids?.length > 0 && counts?.length > 0) {
      // Loop through each subarray of labels, ids, and counts
      for (let i = 0; i < labels?.length; i++) {
        // Create an empty object to map each label to its corresponding id and count
        let labelMap = {};
        // Loop through each label, id, and count in the subarrays
        for (let j = 0; j < labels[i].length; j++) {
          // Assign the id and count as a value to the label as a key
          labelMap[labels[i][j]] = [ids[i][j], counts[i][j]];
        }
        // Create an empty array to store the rearranged subarray of counts
        let rearrangedSubarray = [];
        // Loop through the first subarray of labels as a reference
        for (let k = 0; k < labels[0].length; k++) {
          if (labelMap[labels[0][k]]?.length > 0) {
            let countValue = labelMap[labels[0][k]][1];
            // Push the count value to the rearranged subarray
            rearrangedSubarray.push(countValue);
          }
        }
        // Push the rearranged subarray to the rearranged counts array
        rearrangedCounts.push(rearrangedSubarray);
      }
    }
    // Return the rearranged counts array
    return rearrangedCounts;
  }
  const rearrangedCounts = rearrangeCounts(labels, ids, counts);
  const calculateNormalizedPercentages = (arr, total) => {
    if (arr?.length > 0) {
      if (!isNormalized) {
        // Return raw counts if not normalized
        return arr[0]?.map((_, i) => {
          return arr?.map((row, rowIndex) => {
            return row[i];
          });
        });
      }
      // Return normalized percentages if normalized
      return arr[0]?.map((_, i) => {
        return arr?.map((row, rowIndex) => {
          if (total[rowIndex] !== 0) {
            return Math.floor((row[i] / total[rowIndex]) * 100);
          }
          return 0;
        });
      });
    }
    return [];
  };
  const doughnutPercentages = calculateNormalizedPercentages(
    rearrangedCounts,
    totalReviews
  );
  const categoriesData = categoryLabels?.map((_, categoryIndex) =>
    doughnutPercentages?.map((array, arrayIndex) => array[categoryIndex])
  );
  const menuOptions = [
    {
      id: "1",
      label: t("competitor_analysis.23"),
      value: "1",
      img:
        theme === "dark"
          ? "/assets/display_reviews.svg"
          : "/assets/display_reviews_black.svg",
    },
    {
      id: "2",
      label: t("competitor_analysis.24"),
      value: "2",
      img:
        theme === "dark"
          ? "/assets/display_charts.svg"
          : "/assets/display_charts_black.svg",
    },
  ];
  const handleCategoryClick = (event) => {
    // Tooltip Element
    var tooltipEl = document.getElementById("chartjs-tooltip");
    if (tooltipEl) {
      tooltipEl.remove();
    }
    if (chartInstance.current) {
      const activeElement = chartInstance.current.getElementsAtEventForMode(
        event,
        "nearest",
        { intersect: true },
        true
      );
      if (activeElement.length > 0) {
        const clickedDatasetIndex = activeElement[0].datasetIndex;
        const clickedDataset = data.datasets[clickedDatasetIndex];
        const selectedStackData = {
          label: clickedDataset.label,
          id: clickedDataset.id,
        };
        if (selectedStackData.label && selectedStackData.id) {
          setSelectedCategoryForSubcategories(selectedStackData);
        }
        setSubCategoryClicked(true);
      }
    }
  };
  const handleDisplaySelectionType = (type) => {
    if (type === "1" && selectedTopic) {
      setSelectedGroupForReviews({
        label: dashboardLabels,
        bgColor: "rgb(116, 45, 224)",
        id: dashboardIds.map((item) => parseInt(item)),
        topicId: parseInt(selectedTopic.id),
        topicLabel: selectedTopic.label,
        clickIsFrom: "topics",
      });
    } else if (type === "2" && selectedTopic) {
      displaySubcategoryDoughnuts(selectedTopic.id);
    }
  };

  useEffect(() => {
    if (selectedDisplayType) {
      handleDisplaySelectionType(selectedDisplayType.id);
    }
  }, [selectedDisplayType]);
  const displaySubcategoryDoughnuts = (categoryId) => {
    const clickedDoughnut = {
      label: comparisonData?.map((categoryData) =>
        categoryData?.find((item) => item.categoryId === categoryId)
      )[0]?.category,
      id: categoryId,
    };
    if (clickedDoughnut.label && clickedDoughnut.id) {
      setSelectedCategoryForSubcategories(clickedDoughnut);
    }
    setSubCategoryClicked(true);
  };
  const adjustChartHeight = () => {
    if (dashboardIds?.length > 0) {
      switch (true) {
        case dashboardIds?.length < 5:
          return 300;
        case dashboardIds?.length >= 5 && dashboardIds?.length < 10:
          return 500;
        case dashboardIds?.length >= 10 && dashboardIds?.length < 15:
          return 800;
        case dashboardIds?.length >= 15 && dashboardIds?.length < 20:
          return 1200;
        default:
          return 1500;
      }
    }
  };
  const formatCountsForDoughnuts = (count) => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + "M";
    } else if (count >= 1000) {
      return (count / 1000).toFixed(1) + "K";
    } else {
      return count;
    }
  };
  const hasAlphabet = (str) => {
    return /[A-Za-z]/.test(str);
  };
  const [lineChartDependency, setLineChartDependency] = useState(1);
  useEffect(() => {
    setLineChartDependency(lineChartDependency === 1 ? 2 : 1);
  }, [chartData]);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    interaction: {
      intersect: true,
    },
    scales: {
      y: {
        stacked: true,
        grid: {
          display: false,
          drawOnChartArea: false,
          borderColor: "transparent",
        },
        ticks: {
          display: true,
          color: theme === "dark" ? "white" : "black",
          padding: 10,
          callback: (value) => {
            const label = dashboardLabels[value];
            if (label.length > 15) {
              return label.substring(0, 15) + "...";
            }
            return label;
          },
        },
      },
      x: {
        stacked: true,
        display: false,
        beginAtZero: true,
        autoSkip: false,
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: true,
          color: theme === "dark" ? "white" : "black",
          padding: 10,
        },
        font: {
          size: 14,
        },
      },
    },
    legend: {
      display: false,
    },
    plugins: {
      datalabels: {
        anchor: "center",
        align: "center",
        formatter: function(value, context) {
          if (
            (context.dataset.data[context.dataIndex] >= 0 &&
              context.dataset.data[context.dataIndex] < 1) ||
            isNaN(context.dataset.data[context.dataIndex])
          ) {
            return "";
          }
          if (isNormalized) {
            return context.dataset.data[context.dataIndex] < 3
              ? Math.round(value)
              : Math.round(value) + "%";
          } else {
            return formatCountsForDoughnuts(value);
          }
        },
        color: (context) => {
          const bgColor = context.dataset.backgroundColor;
          if (
            bgColor === "#D2B5F2" ||
            bgColor === "#56DEC8" ||
            bgColor === "#FFBFFF" ||
            bgColor === "#CCCCCC" ||
            bgColor === "#9BC3DE" ||
            bgColor === "#E7EDEF"
          ) {
            return "#4400AA";
          } else {
            return "white";
          }
        },
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          // Tooltip Element
          var tooltipEl = document.getElementById("chartjs-tooltip");

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<div></div>";
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            if (tooltipEl) {
              tooltipEl.remove();
            }
            return;
          }
          // Set caret Position
          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem) {
            return bodyItem.lines;
          }

          // Set Text
          if (tooltipModel.body) {
            let innerHtml = "<div class=" + styles.driversTooltip + ">";
            const index = tooltipModel.dataPoints[0].dataIndex;
            const dataset = tooltipModel.dataPoints[0].dataset;
            const dataPointLabel = tooltipModel.dataPoints[0].label;
            const dataPointValue = tooltipModel.dataPoints[0].formattedValue;
            innerHtml += `
              <div class=${styles.topicLabelInTooltip}>
                <span class=${styles.topicLabel}>${dataPointLabel}</span>
              </div>`;
            innerHtml += `
              <div class=${styles.topicLabelInTooltip}>
                <span class=${styles.fillColor} style="background-color: ${dataset?.backgroundColor}"></span>
                <span class=${styles.topicLabel}>${dataset?.label}: ${dataPointValue}${isNormalized ? '%' : ''}</span>
              </div>`;
            innerHtml += "</div>";
            const tooltipRoot = tooltipEl.querySelector("div");
            tooltipRoot.innerHTML = innerHtml;
          }
          // this will be the overall tooltip
          const position = context.chart.canvas.getBoundingClientRect();

          // Display, position, and set styles for font
          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          let left = position.left + window.pageXOffset + tooltipModel.caretX;
          if (left + tooltipEl.offsetWidth > window.innerWidth) {
            left =
              position.left +
              window.pageXOffset / 2 +
              tooltipModel.caretX -
              tooltipEl.offsetWidth;
          }
          tooltipEl.style.left = left + "px";
          tooltipEl.style.top =
            position.top + window.pageYOffset + tooltipModel.caretY + "px";
          tooltipEl.style.padding =
            tooltipModel.padding + "px " + tooltipModel.padding + "px";
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
    onHover: (event, chartElement) => {
      if (chartElement.length) {
        event.native.target.style.cursor = "pointer";
      } else {
        event.native.target.style.cursor = "default";
      }
    },
    onClick: (event) => {
      handleCategoryClick(event);
    },
  };
  const verticalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: true,
    },
    scales: {
      y: {
        stacked: true,
        display: false,
        beginAtZero: true,
        autoSkip: false,
        grid: {
          display: false,
          drawOnChartArea: false,
          borderColor: "transparent",
        },
      },
      x: {
        stacked: true,
        display: false,
        beginAtZero: true,
        autoSkip: false,
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
          color: theme === "dark" ? "white" : "black",
          padding: 10,
        },
        font: {
          size: 14,
        },
      },
    },
    legend: {
      display: false,
    },
    plugins: {
      datalabels: {
        anchor: "center",
        align: "center",
        formatter: function(value, context) {
          if (
            (context.dataset.data[context.dataIndex] >= 0 &&
              context.dataset.data[context.dataIndex] < 1) ||
            isNaN(context.dataset.data[context.dataIndex])
          ) {
            return "";
          }
          if (isNormalized) {
            return context.dataset.data[context.dataIndex] < 3
              ? Math.round(value)
              : Math.round(value) + "%";
          } else {
            return formatCountsForDoughnuts(value);
          }
        },
        color: (context) => {
          const bgColor = context.dataset.backgroundColor;
          if (
            bgColor === "#D2B5F2" ||
            bgColor === "#56DEC8" ||
            bgColor === "#FFBFFF" ||
            bgColor === "#CCCCCC" ||
            bgColor === "#9BC3DE" ||
            bgColor === "#E7EDEF"
          ) {
            return "#4400AA";
          } else {
            return "white";
          }
        },
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          // Tooltip Element
          var tooltipEl = document.getElementById("chartjs-tooltip");

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<div></div>";
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            if (tooltipEl) {
              tooltipEl.remove();
            }
            return;
          }
          // Set caret Position
          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem) {
            return bodyItem.lines;
          }

          // Set Text
          if (tooltipModel.body) {
            let innerHtml = "<div class=" + styles.driversTooltip + ">";
            const index = tooltipModel.dataPoints[0].dataIndex;
            const dataset = tooltipModel.dataPoints[0].dataset;
            const dataPointLabel = tooltipModel.dataPoints[0].label;
            const dataPointValue = tooltipModel.dataPoints[0].formattedValue;
            const itemData = comparisonData[index].find(
              (item) => item.categoryId === dataset.keyId
            );
            const formattedCount = formatCountsForDoughnuts(itemData.count);
            
            innerHtml += `
              <div class=${styles.topicLabelInTooltip}>
                <span class=${styles.fillColor} style="background-color: ${dataset?.backgroundColor[index]}"></span>
                <span class=${styles.topicLabel}>${dataPointLabel}: ${isNormalized ? dataPointValue + '%' : formattedCount}</span>
              </div>`;
            innerHtml += "</div>";
            const tooltipRoot = tooltipEl.querySelector("div");
            tooltipRoot.innerHTML = innerHtml;
          }
          // this will be the overall tooltip
          const position = context.chart.canvas.getBoundingClientRect();

          // Display, position, and set styles for font
          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          let left = position.left + window.pageXOffset + tooltipModel.caretX;
          if (left + tooltipEl.offsetWidth > window.innerWidth) {
            left =
              position.left +
              window.pageXOffset / 2 +
              tooltipModel.caretX -
              tooltipEl.offsetWidth;
          }
          tooltipEl.style.left = left + "px";
          tooltipEl.style.top =
            position.top + window.pageYOffset + tooltipModel.caretY + "px";
          tooltipEl.style.padding =
            tooltipModel.padding + "px " + tooltipModel.padding + "px";
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
    onHover: (event, chartElement) => {
      if (chartElement.length) {
        event.native.target.style.cursor = "pointer";
      } else {
        event.native.target.style.cursor = "default";
      }
    },
    onClick: (event) => {
      handleCategoryClick(event);
    },
  };

  const renderLegend = (data) => {
    if (chartType === "C2") {
      return data[0]?.labels?.map((label, index) => (
        <div key={index}>
          <span
            className={styles.legendColor}
            style={{
              backgroundColor: data[0]?.datasets[0]?.backgroundColor[index],
            }}
          />
          <span className={styles.legendLabel}>{label}</span>
        </div>
      ));
    }
    return (
      <div className={styles.legendC}>
        {data?.datasets?.map((dataset, index) => (
          <div key={`legend-${index}`} className={styles.legendDisplay}>
            <span
              className={styles.legendColor}
              style={{ backgroundColor: dataset.backgroundColor }}
            />
            <span className={styles.legendLabel}>{dataset.label}</span>
          </div>
        ))}
      </div>
    );
  };
  const handleToggleSelection = (selection) => {
    setToggleSelection(selection);
    setActiveBtn(selection);
  };
  const doughnutDataArray = [];
  const renderChart = () => {
    if (chartType === "C1") {
      if (barType === "H") {
        return (
          <div
            className={styles.HorizontalBar}
            style={{ height: adjustChartHeight() }}
          >
            <CustomChart
              data={data}
              options={options}
              ref={chartInstance}
              theme={theme}
              type={"bar"}
              dependency={[chartData, isNormalized, totalReviews, barType, chartType]}
            />
          </div>
        );
      } else if (barType === "V") {
        return (
          <div
            className={`${styles.verticalBars} ${
              dashboardIds?.length > 3
                ? styles.overflowBars
                : styles.regularBars
            }`}
          >
            <CustomChart
              type={"bar"}
              data={data}
              options={verticalBarOptions}
              ref={chartInstance}
              theme={theme}
              dependency={[chartData, isNormalized, totalReviews, barType, chartType]}
            />
          </div>
        );
      }
    } else if (chartType === "C2") {
      const doughnutCharts = [];
      const zeroDoughnuts = [];
      for (let i = 0; i < categoriesData?.length; i++) {
        let sum = doughnutPercentages[i].reduce((acc, val) => acc + val, 0);
        const dashboardIds = chartData?.map(
          (dashboardData) => dashboardData.dashboardId
        );
        const doughnutChartData = {
          labels: chartData?.map((dashboardData) => dashboardData.dashboard),
          datasets: [
            {
              id: dashboardIds,
              data: doughnutPercentages[i],
              backgroundColor: dashboardIds?.map((id) => doughnutColorMap[id]),
              keyId: categoryIds[i],
              keyLabel: categoryLabels[i],
            },
          ],
        };
        doughnutDataArray.push(doughnutChartData.datasets);
        console.log("don", doughnutDataArray)
        const handleDoughnutClick = (activeElement) => {
          // Tooltip Element
          var tooltipEl = document.getElementById("chartjs-tooltip");
          if (tooltipEl) {
            tooltipEl.remove();
          }
          if (activeElement) {
            const clickedDataset = doughnutChartData.datasets[0];
            const selectedDoughnutData = {
              label: doughnutChartData.labels[activeElement.index],
              bgColor: clickedDataset.backgroundColor[activeElement.index],
              id: [parseInt(clickedDataset.id[activeElement.index])],
              topicId: parseInt(clickedDataset.keyId),
              topicLabel: clickedDataset.keyLabel,
              clickIsFrom: "groups",
            };
            setSelectedGroupForReviews(selectedDoughnutData);
          }
        };
        const doughnutChartOptions = {
          responsive: true,
          interaction: {
            intersect: false,
            mode: "nearest",
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              external: (context) => {
                // Tooltip Element
                var tooltipEl = document.getElementById("chartjs-tooltip");

                // Create element on first render
                if (!tooltipEl) {
                  tooltipEl = document.createElement("div");
                  tooltipEl.id = "chartjs-tooltip";
                  tooltipEl.innerHTML = "<div></div>";
                  document.body.appendChild(tooltipEl);
                }

                // Hide if no tooltip
                const tooltipModel = context.tooltip;
                if (tooltipModel.opacity === 0) {
                  if (tooltipEl) {
                    tooltipEl.remove();
                  }
                  return;
                }
                // Set caret Position
                tooltipEl.classList.remove("above", "below", "no-transform");
                if (tooltipModel.yAlign) {
                  tooltipEl.classList.add(tooltipModel.yAlign);
                } else {
                  tooltipEl.classList.add("no-transform");
                }

                function getBody(bodyItem) {
                  return bodyItem.lines;
                }

                // Set Text
                if (tooltipModel.body) {
                  let innerHtml = "<div class=" + styles.driversTooltip + ">";
                  const index = tooltipModel.dataPoints[0].dataIndex;
                  const dataset = tooltipModel.dataPoints[0].dataset;
                  const datasetLabel = tooltipModel.dataPoints[0].label;
                  const datasetValue = tooltipModel.dataPoints[0].raw;
                  const itemData = comparisonData[index].find(
                    (item) => item.categoryId === dataset.keyId
                  );
                  const formattedCount = formatCountsForDoughnuts(itemData.count);
                  
                  innerHtml += `
                    <div class=${styles.topicLabelInTooltip}>
                      <span class=${styles.fillColor} style="background-color: ${dataset?.backgroundColor[index]}"></span>
                      <span class=${styles.topicLabel}>${datasetLabel}: ${isNormalized ? datasetValue + '%' : formattedCount}</span>
                    </div>`;
                  innerHtml += "</div>";
                  const tooltipRoot = tooltipEl.querySelector("div");
                  tooltipRoot.innerHTML = innerHtml;
                }
                // this will be the overall tooltip
                const position = context.chart.canvas.getBoundingClientRect();

                // Display, position, and set styles for font
                tooltipEl.style.opacity = 1;
                tooltipEl.style.position = "absolute";
                tooltipEl.style.left =
                  position.left +
                  window.pageXOffset +
                  tooltipModel.caretX +
                  "px";
                tooltipEl.style.top =
                  position.top +
                  window.pageYOffset +
                  tooltipModel.caretY +
                  "px";
                tooltipEl.style.padding =
                  tooltipModel.padding + "px " + tooltipModel.padding + "px";
                tooltipEl.style.pointerEvents = "none";
              },
            },
            datalabels: {
              anchor: "center",
              align: "center",
              formatter: (value, context) => {
                if (
                  context.dataset.data[context.dataIndex] == 0 ||
                  context.dataset.data[context.dataIndex] < 1 ||
                  isNaN(context.dataset.data[context.dataIndex])
                ) {
                  return "";
                }
                const itemData = comparisonData[context.dataIndex].find(
                  (item) => item.categoryId === context.dataset.keyId
                );
                const formattedCount = formatCountsForDoughnuts(itemData.count);
                const lineBreak = "\n";
                const spaceBreak = "\t";
                
                if (isNormalized) {
                  return context.dataset.data[context.dataIndex] <= 2
                    ? value + "%"
                    : hasAlphabet(formattedCount)
                    ? `${spaceBreak}${" "}${value + "%"}${lineBreak}(${formattedCount})`
                    : `${" "}${value + "%"}${lineBreak}(${formattedCount})`;
                } else {
                  return hasAlphabet(formattedCount)
                    ? `${spaceBreak}${" "}${formattedCount}`
                    : `${" "}${formattedCount}`;
                }
              },
              color: (context) => {
                const bgColor = context.dataset.backgroundColor[context.dataIndex];
                if (
                  bgColor === "#D2B5F2" ||
                  bgColor === "#56DEC8" ||
                  bgColor === "#FFBFFF" ||
                  bgColor === "#CCCCCC" ||
                  bgColor === "#9BC3DE" ||
                  bgColor === "#E7EDEF"
                ) {
                  return "#4400AA";
                } else {
                  return "white";
                }
              },
            },
          },
          cutoutPercentage: 45,
          maintainAspectRatio: false,
          outerRadius: 45,
          cornerRadius: 7,
          responsive: true,
          aspectRatio: 1,
          elements: {
            arc: {
              borderWidth: 0,
            },
          },
          legend: {
            display: false,
            position: "bottom",
            labels: {
              color: theme === "dark" ? "white" : "black",
            },
          },
          onHover: (event, chartElement) => {
            if (chartElement.length) {
              event.native.target.style.cursor = "pointer";
            } else {
              event.native.target.style.cursor = "default";
            }
          },
          onClick: (e) => {
            const point = e.chart?.getElementsAtEventForMode(e, "nearest", {
              intersect: true,
            });
            if (point?.length > 0) {
              handleDoughnutClick(point[0]);
            }
          },
          scales: {
            y: {
              display: false,
            },
          },
        };

        if (Number(sum) === 0) {
          zeroDoughnuts.push(
            <div className={styles.chartWithLabel}>
              <div className={styles.doughnut}>
                <CustomChart
                  type={"doughnut"}
                  data={doughnutChartData}
                  options={doughnutChartOptions}
                  theme={theme}
                  dependency={chartData}
                />
                {/* <Doughnut
                  data={doughnutChartData}
                  options={doughnutChartOptions}
                  width={230}
                /> */}
                <span className={styles.noDataDoughnutMsg}>
                  {t("competitor_analysis.9")}
                </span>
              </div>
              <h6
                title={categoryLabels[i]}
                className={styles.categoryName}
                onClick={() => displaySubcategoryDoughnuts(categoryIds[i])}
              >
                {categoryLabels[i]}
              </h6>
            </div>
          );
        } else {
          doughnutCharts.push(
            <div className={styles.chartWithLabel} key={`category-${i + 1}`}>
              <div className={styles.doughnut}>
                <CustomChart
                  type={"doughnut"}
                  data={doughnutChartData}
                  options={doughnutChartOptions}
                  theme={theme}
                  ref={(reference) => (chartInstance.current = reference)}
                  dependency={[chartData, isNormalized, totalReviews, chartType]}
                />
                {/* <Doughnut
                  data={doughnutChartData}
                  ref={(reference) => (chartInstance.current = reference)}
                  options={doughnutChartOptions}
                  width={230}
                /> */}
              </div>
              <h6
                title={categoryLabels[i]}
                className={styles.categoryName}
                onClick={() =>
                  setSelectedTopic({
                    id: categoryIds[i],
                    label: categoryLabels[i],
                  })
                }
              >
                <div className={styles.contextMenu}>
                  <DropdownSelect
                    options={menuOptions}
                    selectedOptions={selectedDisplayType}
                    setSelectedOptions={setSelectedDisplayType}
                    hideImg={true}
                    hiddenTriggerLabel={categoryLabels[i]}
                    triggerEvent={"click"}
                  />
                </div>
              </h6>
            </div>
          );
        }
      }
      return [...doughnutCharts, ...zeroDoughnuts];
    } else if (chartType === "C3") {
      let dateArray = [];
      let uniqueAllDates = [];
      if (dateRange && dateRange.length > 0) {
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);
        let currentDate = startDate;
        while (currentDate <= endDate) {
          dateArray.push(new Date(currentDate));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        const allDates = dateArray.map((date) => {
          let year = date.getFullYear();
          let month =
            toggleSelection === "Monthly"
              ? date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                })
              : date.getMonth() + 1;
          let day = date.getDate();

          if (toggleSelection === "Daily") {
            day = day < 10 ? "0" + day : day;
            month = month < 10 ? "0" + month : month;
            return `${day}-${month}-${year}`;
          } else if (toggleSelection === "Monthly") {
            return month.replace(/ /g, "' ");
          } else if (toggleSelection === "Yearly") {
            return `${year}`;
          }
        });
        uniqueAllDates = [...new Set(allDates)];
      } else {
        let allTimeDates = categoryIds?.map((categoryId) => {
          const datesForOneChart = [];
          for (let i = 0; i < comparisonData.length; i++) {
            const dashboardData = comparisonData[i];
            const foundData = dashboardData.find(
              (data) => data.categoryId === categoryId
            );
            if (foundData) {
              const dataByTime =
                foundData.dataByTime[toggleSelection.toLowerCase()];
              // loop through the dataByTime array and replace the zeros with the data values
              for (let j = 0; j < dataByTime.length; j++) {
                const data = dataByTime[j];
                datesForOneChart.push(data.date);
              }
            }
          }
          return datesForOneChart;
        });
        uniqueAllDates = Array.from(new Set([].concat(...allTimeDates)));
      }

      if (uniqueAllDates && uniqueAllDates.length === 1 && uniqueAllDates[0]) {
        let date = new Date(uniqueAllDates[0]);
        let dates = [];
        let prevDate = new Date(date);
        prevDate.setDate(prevDate.getDate() - 1);
        let nextDate = new Date(date);
        nextDate.setDate(nextDate.getDate() + 1);
        if (toggleSelection === "Daily") {
          let prevDay =
            prevDate.getDate() < 10
              ? "0" + prevDate.getDate()
              : prevDate.getDate();
          let prevMonth =
            prevDate.getMonth() + 1 < 10
              ? "0" + (prevDate.getMonth() + 1)
              : prevDate.getMonth() + 1;
          let prevYear = prevDate.getFullYear();
          let nextDay =
            nextDate.getDate() < 10
              ? "0" + nextDate.getDate()
              : nextDate.getDate();
          let nextMonth =
            nextDate.getMonth() + 1 < 10
              ? "0" + (nextDate.getMonth() + 1)
              : nextDate.getMonth() + 1;
          let nextYear = nextDate.getFullYear();
          dates.push(`${prevDay}-${prevMonth}-${prevYear}`);
          dates.push(uniqueAllDates[0]);
          dates.push(`${nextDay}-${nextMonth}-${nextYear}`);
        } else if (toggleSelection === "Monthly") {
          let prevMonth = new Date(date);
          prevMonth.setMonth(prevMonth.getMonth() - 1);
          prevMonth = prevMonth
            .toLocaleDateString("en-US", { year: "numeric", month: "short" })
            .replace(/ /g, "' ");
          let nextMonth = new Date(date);
          nextMonth.setMonth(nextMonth.getMonth() + 1);
          nextMonth = nextMonth
            .toLocaleDateString("en-US", { year: "numeric", month: "short" })
            .replace(/ /g, "' ");
          dates.push(prevMonth);
          dates.push(uniqueAllDates[0]);
          dates.push(nextMonth);
        } else if (toggleSelection === "Yearly") {
          let prevYear = new Date(date);
          prevYear.setFullYear(prevYear.getFullYear() - 1);
          prevYear = prevYear.getFullYear();
          let nextYear = new Date(date);
          nextYear.setFullYear(nextYear.getFullYear() + 1);
          nextYear = nextYear.getFullYear();
          dates.push(`${prevYear}`);
          dates.push(uniqueAllDates[0]);
          dates.push(`${nextYear}`);
        }
        uniqueAllDates = dates;
      }

      const lineChartsData = categoryIds?.map((categoryId) => {
        const dataForOneChart = { labels: [], datasets: [] };
        for (let i = 0; i < comparisonData.length; i++) {
          const dashboardData = comparisonData[i];
          const foundData = dashboardData.find(
            (data) => data.categoryId === categoryId
          );
          if (foundData) {
            const dashboardDataset = {
              label: dashboardLabels[i],
              data: [], // initialize the data array with zeros
              backgroundColor: doughnutColorMap[dashboardIds[i]],
              fill: false,
              borderColor: doughnutColorMap[dashboardIds[i]],
              lineTension: 0.6,
              borderWidth: 3,
              pointRadius: 0,
              pointHoverRadius: isMobileView ? 1 : 4,
            };
            // fill the data array with zeros based on the uniqueAllDates length
            dashboardDataset.data = Array(uniqueAllDates.length).fill(0);
            // use the toggleSelection to determine which dataByTime property to use
            const dataByTime =
              foundData.dataByTime[toggleSelection.toLowerCase()];
            // loop through the dataByTime array and replace the zeros with the data values
            for (let j = 0; j < dataByTime.length; j++) {
              const data = dataByTime[j];
              // find the index of the data date in the uniqueAllDates array
              const index = uniqueAllDates.indexOf(data.date);
              // replace the zero at that index with the data count
              dashboardDataset.data[index] = data.count;
            }
            // copy the uniqueAllDates array to the dataForOneChart labels
            dataForOneChart.labels = [...uniqueAllDates];
            // push the dashboardDataset to the dataForOneChart datasets
            dataForOneChart.datasets.push(dashboardDataset);
          }
        }
        return dataForOneChart;
      });
      const updatedCategoryData = lineChartsData?.reduce(
        (result, chartData, index) => {
          const categoryName = categoryLabels[index];
          const categoryId = categoryIds[index];
          if (
            chartData?.datasets
              ?.map((d) => d.data)
              .flat()
              .every((element) => element === 0)
          ) {
            result.emptyCharts.push({ chartData, categoryName, categoryId });
          } else {
            result.nonEmptyCharts.push({ chartData, categoryName, categoryId });
          }

          return result;
        },
        { emptyCharts: [], nonEmptyCharts: [] }
      );

      const finalCategoryData = [
        ...updatedCategoryData.nonEmptyCharts,
        ...updatedCategoryData.emptyCharts,
      ];
      const lineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        hover: {
          mode: "nearest",
          intersect: false,
        },
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          tooltip: {
            enabled: false,
            external: (context) => {
              // Tooltip Element
              var tooltipEl = document.getElementById("chartjs-tooltip");

              // Create element on first render
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
                tooltipEl.innerHTML = "<div></div>";
                document.body.appendChild(tooltipEl);
              }

              // Hide if no tooltip
              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                if (tooltipEl) {
                  tooltipEl.remove();
                }
                return;
              }
              // Set caret Position
              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              function getBody(bodyItem) {
                return bodyItem.lines;
              }

              // Set Text
              if (tooltipModel.body) {
                let innerHtml = "<div class=" + styles.driversTooltip + ">";
                const dataPointLabel = tooltipModel.dataPoints[0].label;
                innerHtml += `
                <div class=${styles.topicLabelInTooltip}>
                  <span class=${styles.topicLabel}>${dataPointLabel}</span>
                </div>`;
                tooltipModel.dataPoints.forEach((dataPoint) => {
                  const dataset = dataPoint.dataset;
                  const datasetLabel = dataset.label;
                  const dataPointValue = dataPoint.raw;
                  innerHtml += `
                    <div class=${styles.topicLabelInTooltip}>
                      <span class=${styles.fillColor} style="background-color: ${dataset?.borderColor}"></span>
                      <span class=${styles.topicLabel}>${datasetLabel}: ${dataPointValue}</span>
                    </div>`;
                });
                innerHtml += "</div>";
                const tooltipRoot = tooltipEl.querySelector("div");
                tooltipRoot.innerHTML = innerHtml;
              }
              // this will be the overall tooltip
              const position = context.chart.canvas.getBoundingClientRect();

              // Display, position, and set styles for font
              tooltipEl.style.opacity = 1;
              tooltipEl.style.position = "absolute";
              tooltipEl.style.left =
                position.left +
                window.pageXOffset +
                tooltipModel.caretX -
                40 +
                "px";
              tooltipEl.style.top =
                position.top +
                window.pageYOffset +
                tooltipModel.caretY -
                80 +
                "px";
              tooltipEl.style.padding =
                tooltipModel.padding + "px " + tooltipModel.padding + "px";
              tooltipEl.style.pointerEvents = "none";
            },
          },
          datalabels: false,
          legend: {
            display: false,
            labels: {
              usePointStyle: true,
              fontColor: theme === "dark" ? "white" : "black",
              pointStyle: "circle",
              padding: 20,
            },
            borderRadius: 20,
            position: "bottom",
          },
        },
        scales: {
          x: {
            display: true,
            grid: {
              color: theme === "dark" ? "white" : "black",
              borderColor:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              display: true,
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              color: theme === "dark" ? "white" : "black",
              padding: 15,
            },
          },
          y: {
            display: true,
            grid: {
              display: true,
              color: theme === "dark" ? "white" : "black",
              borderColor:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              drawTicks: false,
              drawOnChartArea: false,
            },
            ticks: {
              beginAtZero: true,
              display: true,
              padding: 15,
              color: theme === "dark" ? "white" : "black",
            },
            title: {
              display: true,
              text: t("competitor_analysis.19"),
              color: theme === "dark" ? "white" : "black",
            },
            afterDataLimits: (axis) => {
              const max = axis.max;
              let addVal = 0;
              if (max < 5) {
                addVal = 2;
              } else if (max < 10) {
                addVal = 5;
              } else if (max < 100) {
                addVal = 10;
              } else if (max < 1000) {
                addVal = 50;
              } else if (max < 10000) {
                addVal = 500;
              } else if (max < 100000) {
                addVal = 1000;
              } else if (max < 1000000) {
                addVal = 5000;
              } else {
                addVal = 10000;
              }
              axis.max += addVal;
            },
          },
        },
      };

      const emptyLineOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          datalabels: {
            display: false,
          },
        },
        scales: {
          x: {
            color: theme === "dark" ? "white" : "black",
            grid: {
              display: true,
              borderColor:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              color:
                theme === "dark"
                  ? "rgba(128, 128, 128, 0.4)"
                  : "rgba(0, 0, 0, 0.2)",
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              color: theme === "dark" ? "white" : "black",
              padding: 20,
            },
          },
          y: {
            display: false,
            color: theme === "dark" ? "white" : "black",
            beginAtZero: true,
            grid: {
              display: true,
              borderColor:
                theme === "dark"
                  ? "rgba(255, 255, 255, 0.4)"
                  : "rgba(0, 0, 0, 0.4)",
              color:
                theme === "dark"
                  ? "rgba(128, 128, 128, 0.4)"
                  : "rgba(0, 0, 0, 0.2)",
              drawOnChartArea: false,
              drawTicks: false,
            },
            ticks: {
              display: false,
              padding: 20,
              color: theme === "dark" ? "white" : "black",
              fontSize: isMobileView ? 10 : 12,
            },
            afterDataLimits: (axis) => {
              const max = axis.max;
              let addVal = 0;
              if (max < 5) {
                addVal = 2;
              } else if (max < 10) {
                addVal = 5;
              } else if (max < 100) {
                addVal = 10;
              } else if (max < 1000) {
                addVal = 50;
              } else if (max < 10000) {
                addVal = 500;
              } else if (max < 100000) {
                addVal = 1000;
              } else if (max < 1000000) {
                addVal = 5000;
              } else {
                addVal = 10000;
              }
              axis.max += addVal;
            },
          },
        },
      };
      const displaySubcategoryLines = (categoryId) => {
        const clickedLine = {
          label: comparisonData?.map((categoryData) =>
            categoryData?.find((item) => item.categoryId === categoryId)
          )[0]?.category,
          id: categoryId,
        };
        if (clickedLine.label && clickedLine.id) {
          setSelectedCategoryForSubcategories(clickedLine);
        }
        setSubCategoryClicked(true);
      };
      return (
        <div className={styles.chartContainer}>
          <div className={styles.toggleBtnContainer}>
            <Button
              className={
                activeBtn === "Daily"
                  ? styles.activeToggleBtn
                  : styles.toggleBtn
              }
              onClick={() => handleToggleSelection("Daily")}
            >
              {t("competitor_analysis.20")}
            </Button>
            <Button
              className={
                activeBtn === "Monthly"
                  ? styles.activeToggleBtn
                  : styles.toggleBtn
              }
              onClick={() => handleToggleSelection("Monthly")}
            >
              {t("competitor_analysis.21")}
            </Button>
            <Button
              className={
                activeBtn === "Yearly"
                  ? styles.activeToggleBtn
                  : styles.toggleBtn
              }
              onClick={() => handleToggleSelection("Yearly")}
            >
              {t("competitor_analysis.22")}
            </Button>
          </div>
          <div className={styles.lineChartContainer}>
            {finalCategoryData?.map(
              ({ chartData, categoryName, categoryId }, index) => (
                <div>
                  {!chartData?.datasets
                    ?.map((d) => d.data)
                    .flat()
                    .every((element) => element === 0) ? (
                    <div className={styles.chartWithLabel}>
                      <span
                        title={categoryName}
                        className={styles.categoryName}
                        onClick={() =>
                          setSelectedTopic({
                            id: categoryId,
                            label: categoryName,
                          })
                        }
                      >
                        <div className={styles.contextMenu}>
                          <DropdownSelect
                            options={menuOptions}
                            selectedOptions={selectedDisplayType}
                            setSelectedOptions={setSelectedDisplayType}
                            hideImg={true}
                            hiddenTriggerLabel={categoryName}
                          />
                        </div>
                      </span>
                      <div className={styles.lineChart}>
                        <CustomChart
                          type={"line"}
                          data={{
                            ...chartData,
                            datasets: chartData.datasets.map((d) => ({
                              ...d,
                              hidden: d.data.every((x) => x === 0),
                            })),
                          }}
                          options={lineOptions}
                          dependency={lineChartDependency}
                          plugins={[
                            {
                              beforeDraw: (chart) => {
                                const { ctx, tooltip, chartArea } = chart;
                                if (tooltip?.dataPoints?.length > 0) {
                                  ctx.save();
                                  const activePoint = tooltip.dataPoints[0];
                                  const x = activePoint.element.x;
                                  ctx.beginPath();
                                  ctx.setLineDash([4, 6]);
                                  ctx.moveTo(x, chartArea.top);
                                  ctx.lineTo(x, chartArea.bottom);
                                  ctx.lineWidth = 2;
                                  ctx.strokeStyle = "rgba(155, 155, 155)";
                                  ctx.stroke();
                                  ctx.restore();
                                }
                              },
                            },
                          ]}
                        />
                      </div>
                      <div className={styles.lineChartLegend}>
                        {chartData.datasets.map((d, index) => (
                          <div
                            key={`legend-${index}`}
                            style={{
                              textDecoration: d.data.every((x) => x === 0)
                                ? "line-through"
                                : "none",
                            }}
                          >
                            <span
                              className={styles.legendColor}
                              style={{ backgroundColor: d.backgroundColor }}
                            />
                            <span className={styles.legendLabel}>
                              {d.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.chartWithLabel}>
                      <span
                        title={categoryName}
                        className={styles.categoryName}
                        onClick={() => displaySubcategoryLines(categoryId)}
                      >
                        {categoryName}
                      </span>
                      <div className={styles.emptyLineChart}>
                        <CustomChart
                          type={"line"}
                          data={{
                            ...chartData,
                            datasets: chartData.datasets.map((d) => ({
                              ...d,
                              hidden: true,
                            })),
                          }}
                          options={emptyLineOptions}
                          dependency={chartData.datasets}
                          theme={theme}
                        />
                        <span className={styles.noDataMsg}>
                          {t("competitor_analysis.9")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      );
    }
  };
  return (
    <>
      <div className={styles.Chart}>{renderChart()}</div>
      <div className={styles.Legend}>
        {chartType === "C1" 
          ? renderLegend(data)
          : renderLegend(doughnutDataArray)}
      </div>
    </>
  );
};

export default ComparisonChart;
